import { when } from 'mobx'
import type { RemoteTrackPublication, Room } from 'livekit-client'
import type { MeetingCubit } from '../MeetingCubit'
import {
  DeadAirDetector,
  createLivekitDeadAirEnvironment,
  normalizeSink,
  type DeadAirEnvironment,
  type OutputDeviceSnapshot,
} from './DeadAirDetector'
import {
  AudioEpisodeSink,
  buildDeadAirCondition,
  resolveNudgeCause,
  type DeadAirNudgeMode,
  type DeadAirNudgeRequest,
  type HealVerdict,
} from '../../util/audioEpisodeSink'
import type { DeadAirEpisode, DeadAirFork } from '../../util/deadAirTypes'
import {
  AutoHealState,
  matchWiringFix,
  type AutoHealFix,
} from '../../util/autoHeal'
import { TelemetrySampler } from '../../util/telemetry/telemetrySampler'
import { AudioTelemetryBuffer } from './AudioTelemetryBuffer'
import { AudioObservabilitySources } from './AudioObservabilitySources'
import {
  unpackAudioReceiverStats,
  type AudioReceiverStats,
} from '../../util/audioReceiverStats'

/**
 * DeadAirController — owns the inbound dead-air detection stack for one room.
 *
 * A sibling collaborator of MeetingLivekitController rather than more methods
 * on it: this is a self-contained lifecycle (a detector, a sink, a context ring
 * and the sources that fill it, a device cache, a settings-arrival retry, and
 * their teardown) whose only contact with the rest of the room is four calls
 * the controller forwards.
 *
 * It is long-lived and non-React on purpose. The failure moment being
 * diagnosed is a slide-transition re-render that unmounts components, so
 * diagnostic state kept in React state would be destroyed by the very event it
 * exists to observe.
 *
 * The whole stack is gated on `detectionEnabled`. With it off nothing is
 * constructed, no listeners are registered and no timer runs.
 */

/**
 * The peer identity a QA-injected episode is attributed to. It rides along in
 * the GA `deadPeers` column and in the Sentry nudge bundle, so synthetic episodes
 * can be excluded from the calibration numbers the report-only window exists
 * to produce.
 */
export const QA_SYNTHETIC_IDENTITY = 'qa_injected_not_a_real_peer'

/**
 * A dead peer on its way to the dialog. The fork travels with the identity
 * because the dialog's telemetry names one failure type and an episode can hold
 * several: peer A healing silently while peer B escalates is the ordinary mixed
 * case, and a cause taken from the episode would report A's fault for a dialog
 * that only exists because of B.
 */
type NudgePeer = { identity: string; fork: DeadAirFork }

/** One peer's cheap fix, in flight and awaiting its verify pass. */
type HealAttempt = NudgePeer & { fix: AutoHealFix }

/**
 * The forks QA can stage from inside the app.
 *
 * Five of the eight, and the missing three are missing for different reasons.
 * `energy_unavailable` and `inconclusive` are the detector's two "cannot tell"
 * exits, so staging them means building a state the classifier is defined not
 * to name. `silent_decode` has no honest stager — see {@link
 * DeadAirController.qaToggleFault} for the arithmetic.
 */
export type DeadAirQaFault = Extract<
  DeadAirFork,
  | 'not_subscribed'
  | 'not_attached'
  | 'lost_play'
  | 'sink_divergence'
  | 'delivery_stalled'
>

/**
 * What a toggle did.
 *
 * `refused` is a first-class outcome rather than a throw or a null. Every fault
 * has preconditions the room may not meet — no peer publishing, no second
 * output device, a browser without `setSinkId` — and a tester needs to know
 * which one blocked them. A silent no-op is indistinguishable from a fault that
 * armed and went unnoticed, which is the exact ambiguity this whole surface
 * exists to remove.
 */
export type QaFaultResult =
  | { outcome: 'armed'; detail: string }
  /** `ok` is false when the undo itself failed, e.g. a refused `play()`. */
  | { outcome: 'cleared'; detail: string; ok: boolean }
  | { outcome: 'refused'; detail: string }

/** One peer's audio, as the stagers need to see it. */
type QaPeerAudio = {
  identity: string
  publications: RemoteTrackPublication[]
  elements: HTMLMediaElement[]
}

/** An armed fault and the undo that captured what it broke. */
type StagedQaFault = {
  detail: string
  undo: () => Promise<{ ok: boolean; detail: string }>
}

/** Which optional `inbound-rtp` fields one peer's receiver actually exposes. */
export type QaReceiverStatsReport = {
  identity: string
  stats: AudioReceiverStats | undefined
  hasEnergy: boolean
  hasConcealment: boolean
  hasSamplesReceived: boolean
  canReportSilentDecode: boolean
  canReportDeliveryStalled: boolean
}

/**
 * An element that may support output routing. `setSinkId` is not on
 * `HTMLMediaElement` in lib.dom for every target we build against, and Safari
 * only grew it recently, so it is read defensively at every call site.
 */
function asSinkable(el: HTMLMediaElement): HTMLMediaElement & {
  sinkId?: string
  setSinkId?: (id: string) => Promise<void>
} {
  return el
}

export class DeadAirController {
  private detector: DeadAirDetector | null = null
  private sink: AudioEpisodeSink | null = null
  private sources: AudioObservabilitySources | null = null
  private env: DeadAirEnvironment | null = null
  private room: Room | null = null
  private retryArmed = false
  private disposers: (() => void)[] = []

  /** Per-peer cheap-repair discipline (one attempt per break, re-arm on recovery). */
  private readonly autoHealState = new AutoHealState()
  /**
   * When each peer's last cheap fix was dispatched. Read by the detector's
   * stats probe only, to say whether a fix landed between its reads.
   */
  private readonly lastHealAt = new Map<string, number>()
  /** Pending settle-then-verify timers, cleared on dispose. */
  private readonly autoHealTimers = new Set<ReturnType<typeof setTimeout>>()

  /** Track sids that already fired room_track_subscription_failed. */
  private readonly failedSubscriptionSids = new Set<string>()

  /**
   * QA faults currently staged against this room, each holding the undo that
   * captured what it broke.
   *
   * Faults compose deliberately, so this is a map rather than a single slot: a
   * peer with two simultaneous problems is a production case nobody has watched
   * the ladder handle, and the only way to stage one is to arm two.
   */
  private readonly qaStagedFaults = new Map<DeadAirQaFault, StagedQaFault>()

  /**
   * Cached output-device list for the wiring snapshot. enumerateDevices is
   * async and the structural walk is synchronous, so it is refreshed ahead of
   * time on `devicechange`.
   */
  private outputSnapshot: OutputDeviceSnapshot = {
    audioOutputs: [],
    lastDeviceChangeAt: null,
  }

  private deviceChangeUnsub: (() => void) | null = null

  /**
   * Whether {@link DeadAirController.runReconnect} is between tearing the room
   * down and handing back its verdict.
   *
   * The detector reads this to tell a subscription this client dropped on
   * purpose from one that went missing; see
   * {@link DeadAirEnvironment.isRepairInFlight}. Held here rather than derived
   * from the room because `reconnectRoom` never puts LiveKit into a
   * reconnecting state — it disconnects and connects, which reads as an
   * ordinary connect from the outside.
   */
  private repairInFlight = false

  constructor(private meeting: MeetingCubit) {}

  /**
   * Read through the model's guarded getter, not `.data`. A settings document
   * that does not exist streams in as `data: undefined`, and every read here
   * runs during a React render (`attach` is reached from
   * SharedLiveKitProvider), so an undefined read takes the whole meeting page
   * down with the error boundary. The guarded getter falls back to the schema
   * defaults, which is detection off.
   */
  private get settings() {
    return this.meeting.repository.deadAirSettings.settings
  }

  /** Whether the stack is up (detection enabled and wired to a room). */
  get isRunning(): boolean {
    return this.detector !== null
  }

  /**
   * Stand up the detector and the episode sink for this room. Idempotent —
   * only wires once per room.
   */
  attach(room: Room): void {
    if (this.detector) return
    if (!this.settings.detectionEnabled) {
      // The settings document may not have arrived yet: a student who lands
      // directly on a meeting URL can connect to LiveKit before the Firestore
      // snapshot streams in, and this is the only moment setup is attempted.
      // Reading the default `false` would switch detection off for the whole
      // session, silently, on a call where it was enabled.
      this.armRetry(room)
      return
    }

    // Thresholds come from the settings document, so detection can be
    // recalibrated without a deploy. Unset fields fall back to the schema
    // defaults.
    //
    // Read once, here, and baked into the detector for the life of the room: a
    // Firestore edit mid-meeting does not reach a running detector, and only a
    // teardown and reattach picks it up. That is the intended trade — these
    // thresholds decide what counts as dead air, and swapping them under a
    // detector that has already baselined a speaker would compare a window
    // measured under one calibration against a verdict made under another.
    // The next room gets the new values.
    const settings = this.settings

    const ring = new AudioTelemetryBuffer({
      windowMs: settings.ringWindowMS,
      maxEntries: settings.ringMaxEntries,
    })
    const sink = new AudioEpisodeSink({
      logEvent: (name, params) => this.logGaEvent(name, params),
      // The episode cadence is a setting for the same reason the thresholds
      // are: it is the floor under `nudgeWaitDurationMS`, so an operator who
      // cannot move it cannot move the dialog below 30s however low the wait
      // is set. It gates the GA scan row and the episode breadcrumb too;
      // see the schema comment on `episodeMinIntervalMS`.
      sampler: new TelemetrySampler({
        anomalyMinIntervalMs: settings.episodeMinIntervalMS,
      }),
      ring,
      // Drive the user-facing recovery nudge from every confirmed episode. The
      // cause policy (which episodes are worth a nudge, and how the dialog
      // heals) lives here, not in the sink.
      onEpisode: (episode) => this.onEpisode(episode),
    })
    this.sink = sink
    this.room = room

    this.startOutputDeviceCache(room)

    const env = createLivekitDeadAirEnvironment(room, {
      listenerId: () => this.meeting.currentUser.uid,
      pageHidden: () => typeof document !== 'undefined' && document.hidden,
      hadSubscriptionFailure: (sid) => this.failedSubscriptionSids.has(sid),
      isListenerSequestered: () => this.meeting.isSequestered,
      isRepairInFlight: () => this.repairInFlight,
      lastHealAt: (identity) => this.lastHealAt.get(identity),
      // Set by `LiveKitRoomWrapper`'s connected handler, which is the meeting
      // UI mounting — not by `setLivekitRoom`, which is the AV check. That gap
      // is the whole point of the gate: `attach` below runs at the earlier
      // moment. Never reset, so a reconnect does not re-close it.
      hasListenerJoined: () => this.meeting.livekitRoomReady,
      outputDevices: () => ({
        ...this.outputSnapshot,
        activeSinkId: room.getActiveDevice('audiooutput'),
      }),
    })
    this.env = env

    const detector = new DeadAirDetector({
      env,
      sink,
      frozenPlayheadMs: settings.frozenPlayheadMS,
      attachGraceMs: settings.attachGraceMS,
      sustainedSpeakerMs: settings.sustainedSpeakerMS,
      speakerFlickerGapMs: settings.speakerFlickerGapMS,
      backstopIntervalMs: settings.backstopIntervalMS,
      energyEpsilon: settings.energyEpsilon,
      concealFraction: settings.concealFraction,
      lostPlayProbeGapMs: settings.lostPlayProbeGapMS,
      lostPlayProbeSweepEnabled: settings.lostPlayProbeSweepEnabled,
      onRecovered: () => {
        // A genuine recovery re-arms auto-heal: the next break is a fresh
        // attempt.
        this.autoHealState.reset()
        this.meeting.localCommands.emit('deadAirRecovered')
      },
    })
    detector.attach(room)
    this.detector = detector

    // The context sources fill the ring: livekit's own warnings, remote-track
    // mute, page lifecycle, and element stall events.
    const sources = new AudioObservabilitySources({ ring })
    sources.attach(room)
    this.sources = sources

    // A pending episode must survive the reload it is trying to record.
    sink.installReloadFlush()
  }

  /**
   * GA transport for the two dead-air rows, carrying the same room identifiers
   * every other meeting event does.
   *
   * The Admin Inspector builds a room's timeline from GA events that carry
   * `room_id` (`functions/src/inspector/queries/getRoomLogs.ts`). Without one,
   * the single event that says dead air was detected in a room is missing from
   * the tool you open to investigate that room.
   *
   * The identifiers are copied here rather than routing through
   * `MeetingCubit.logEvent`, which injects them already, because that wrapper
   * also calls `markActivity()`. That feeds the 90 minute idle timeout whose
   * whole premise is that nothing happened in the meeting — and a dead-air scan
   * every 30 seconds would keep a genuinely abandoned room alive forever.
   */
  private logGaEvent(name: string, params: Record<string, unknown>): void {
    this.meeting.repository.logEvent(name, {
      room_id: this.meeting.roomId,
      assignment_id: this.meeting.assignmentId,
      section_id: this.meeting.sectionId,
      slide_deck_id: this.meeting.slideDeck.id,
      active_slide: this.meeting.activeSlide,
      ...params,
    })
  }

  /** Wiring scan on the slide transition — the commonest failure moment. */
  onSlideChange(): void {
    this.detector?.onSlideChange()
  }

  /**
   * Feed the flow checks. They accumulate each remote speaker's sustained time
   * and spend a single scoped getStats once one crosses the threshold.
   */
  onActiveSpeakers(identities: string[]): void {
    void this.detector?.onActiveSpeakers(
      identities.map((identity) => ({ identity }))
    )
  }

  /**
   * Remember a track whose subscription failed loudly, so the detector reports
   * it as an existing error rather than a fresh silent `not_subscribed`.
   */
  noteSubscriptionFailed(trackSid: string): void {
    this.failedSubscriptionSids.add(trackSid)
  }

  /**
   * The subscription succeeded, so the earlier failure is spent. We drop the
   * sid rather than leave it, because leaving it would suppress
   * `not_subscribed` for that publication for the rest of the session — so a
   * later silent SFU drop, the exact case that failure type exists to catch,
   * would read as a healthy peer.
   */
  noteSubscribed(trackSid: string | undefined): void {
    if (trackSid) this.failedSubscriptionSids.delete(trackSid)
  }

  dispose(): void {
    this.detector?.detach()
    this.detector = null
    this.sources?.detach()
    this.sources = null
    this.sink?.removeReloadFlush()
    this.sink = null
    this.env = null
    this.room = null
    this.deviceChangeUnsub?.()
    this.deviceChangeUnsub = null
    for (const dispose of this.disposers) dispose()
    this.disposers = []
    this.failedSubscriptionSids.clear()
    // The undos are dropped rather than run: they act on a room that is being
    // torn down, and re-subscribing or re-attaching against it would either
    // throw or resurrect state the teardown just released.
    this.qaStagedFaults.clear()
    for (const timer of this.autoHealTimers) clearTimeout(timer)
    this.autoHealTimers.clear()
    this.autoHealState.reset()
  }

  /**
   * Decide what a confirmed episode does for the user. The episode has already
   * written its GA row by the time this runs (report-first), so a peer that
   * the cheap fix silently repairs is still recorded.
   *
   * With auto-heal on, a wiring fault gets one fault-matched per-peer repair
   * before any dialog; only a fault with no cheap fix, or one whose fix failed,
   * escalates to the dialog. With auto-heal off, everything routes to the
   * dialog.
   */
  private onEpisode(episode: DeadAirEpisode): void {
    const cause = resolveNudgeCause(episode)
    // Logged before the early return, so an episode that resolves to no cause
    // is visibly different from one that never happened.
    this.debugLog('episode', {
      cause: cause ?? 'none',
      deadPeers: episode.deadPeers.map((p) => `${p.identity}:${p.fork}`),
      peersSubscribed: episode.peersSubscribed,
      peersPublishing: episode.peersPublishing,
      connectionState: episode.connectionState,
    })
    if (!cause) return

    if (!this.settings.autoHealEnabled) {
      this.escalateToDialog(episode.deadPeers)
      return
    }

    const attemptable: HealAttempt[] = []
    const escalateNow: NudgePeer[] = []
    for (const peer of episode.deadPeers) {
      const fix = matchWiringFix(peer.fork, this.env?.isWebKit ?? false)
      if (this.autoHealState.isInFlight(peer.identity)) {
        // A fix is already landing for this peer; its verify pass owns the
        // escalation, so a slide change can't double-fire the fix.
        continue
      }
      if (fix && this.autoHealState.shouldAttempt(peer.identity)) {
        attemptable.push({ identity: peer.identity, fork: peer.fork, fix })
      } else {
        // No cheap fix (flow fault, missing subscription, nothing attached), or
        // this peer's one cheap fix is already spent — hand them to the dialog.
        escalateNow.push({ identity: peer.identity, fork: peer.fork })
      }
    }

    // The cycle these attempts belong to, taken here rather than when the
    // verify is armed. The fixes are awaited in between, and a recovery landing
    // in that gap re-arms the ladder: a token read afterwards would be the new
    // cycle's, and the verify would spend these peers against a cycle its fix
    // had no part in — the next real break for them would skip the cheap fix
    // and go straight to the dialog.
    const token = this.autoHealState.cycleToken

    // Marking in-flight is synchronous, before any fix is applied, so a slide
    // change during a fix that is still landing can't double-fire it.
    const applied = attemptable.map((attempt) => {
      this.autoHealState.markInFlight(attempt.identity)
      this.lastHealAt.set(attempt.identity, Date.now())
      return this.applyWiringFix(attempt.identity, attempt.fix)
    })
    if (attemptable.length > 0) {
      void this.afterFixesLand(applied).then(() =>
        this.scheduleHealVerify(attemptable, token)
      )
    }
    this.escalateToDialog(escalateNow)
  }

  /**
   * Wait for the applied fixes to finish before the settle window starts.
   *
   * `setSinkId` is a promise, and the resink fix is two of them in sequence.
   * Timing the settle window from dispatch instead meant the second call could
   * still be outstanding when the verify measured — on Firefox, where setSinkId
   * is behind a flag, routinely so — and a fix that worked was recorded as a
   * no-op, biasing the heal telemetry against sink repair.
   *
   * The wait is capped at the settle window: a `setSinkId` that never settles
   * would otherwise leave the peer in-flight forever, never verified and never
   * escalated, which is worse than measuring early.
   *
   * That cap is a second budget of `autoHealSettleMS`, spent before the verify
   * timer starts, so a fix that never settles takes ~2x the setting end to end
   * rather than the one window the name suggests. It is the intended ceiling —
   * the two windows measure different things, landing and settling — but the
   * worst case is worth stating: the peer waits twice the configured value
   * before the dialog is offered.
   */
  private afterFixesLand(applied: Promise<void>[]): Promise<unknown> {
    const capMs = this.settings.autoHealSettleMS
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.autoHealTimers.delete(timer)
        resolve(undefined)
      }, capMs)
      this.autoHealTimers.add(timer)
      void Promise.allSettled(applied).then(() => {
        clearTimeout(timer)
        this.autoHealTimers.delete(timer)
        resolve(undefined)
      })
    })
  }

  /**
   * Emit the debounced `deadAir` command the UI turns into the dialog, choosing
   * which repair it offers.
   *
   * This is the only place the rung is decided, which matters because the
   * auto-heal-disabled path above returns early: deciding it there would leave a
   * room with the cheap fix turned off offering the reconnect over and over.
   *
   * It is also the only place the reported cause is decided, and it takes it
   * from the peers being escalated rather than from the episode they came out
   * of. The two differ whenever an episode mixes a peer the cheap fix handles
   * with one it does not, and the episode's version names the wrong fault.
   *
   * The same is true of `forks` and `escalatedCondition`: everything this
   * method reports describes the peers being escalated, never the episode.
   *
   * `cause` is the first escalated peer's fork and the whole set rides beside
   * it, because one dialog can stand for peers that failed differently: peer A
   * `not_attached` and peer B `delivery_stalled` escalate together, and
   * reporting only A's fork left B's fault absent from every `dead_air_nudge_*`
   * event. Ranking them instead would mean deciding which of two unrelated
   * faults is worse, which nothing here knows; splitting them into a command
   * each would open a dialog per fork for a client that needs one.
   */
  private escalateToDialog(peers: NudgePeer[]): void {
    const cause = peers[0]?.fork
    if (!cause) return
    const mode: DeadAirNudgeMode = this.autoHealState.shouldOfferReconnect()
      ? 'reconnect'
      : 'reload'
    // Sorted so the same set reads the same way on every row, whatever order
    // the roster walk happened to condemn them in.
    const forks = [...new Set(peers.map((p) => p.fork))].sort()
    // The escalated peers, not the episode's. Named for the subset it is: the
    // sink's `condition` is every dead peer, and one name over two populations
    // makes one fault count as two.
    const escalatedCondition = buildDeadAirCondition(peers)
    this.debugLog('escalate', {
      cause,
      forks,
      mode,
      escalatedCondition,
      identities: peers.map((p) => p.identity),
    })
    this.meeting.localCommands.emit('deadAir', {
      cause,
      forks,
      identities: peers.map((p) => p.identity),
      escalatedCondition,
      mode,
    })
  }

  /**
   * Apply the fault-matched cheap fix to one dead peer's audio track, and
   * resolve once it has actually landed. Every step is best-effort and guarded:
   * a throw or a rejection here must not break the room, and the verify pass
   * will find the peer still dead and escalate.
   */
  private async applyWiringFix(
    identity: string,
    fix: AutoHealFix
  ): Promise<void> {
    const room = this.room
    if (!room) return
    const participant = room.remoteParticipants.get(identity)
    if (!participant) return
    this.debugLog('heal_fix', { identity, fix })
    for (const pub of participant.audioTrackPublications.values()) {
      const track = pub.audioTrack
      if (!track) continue
      const pending: Promise<unknown>[] = []
      try {
        if (fix === 'play') {
          for (const el of track.attachedElements) {
            const played = (el as HTMLMediaElement).play()
            if (played) pending.push(played.catch(() => {}))
          }
        } else if (fix === 'resink') {
          const active = room.getActiveDevice('audiooutput')
          for (const el of track.attachedElements) {
            const media = el as HTMLMediaElement & {
              setSinkId?: (id: string) => Promise<void>
            }
            if (!media.setSinkId) continue
            pending.push(
              media
                .setSinkId('')
                .then(() => (active ? media.setSinkId?.(active) : undefined))
                .catch(() => {})
            )
          }
        }
      } catch {
        // Best-effort; the verify pass handles a failed fix.
      }
      await Promise.allSettled(pending)
      // One audio publication per remote participant, so the first one with a
      // decoded track is the only one there is. Screen share is published
      // without audio (`setScreenShareEnabled` is called with no options, and
      // livekit defaults `audio` to false), which is the only thing that would
      // give a participant a second audio track. If that changes, this walk
      // has to repair all of them: the dead-air scan condemns a peer, not a
      // publication, so fixing one and stopping would leave the verify
      // reporting the peer still dead with no indication why.
      //
      // The break lands whether or not the track had elements to act on. With
      // one publication there is nothing else to walk to anyway, and an empty
      // `attachedElements` here means the element went away between the scan
      // that condemned the peer and this fix — a real fault, not one either fix
      // can repair. The verify pass finds the peer still dead and escalates,
      // which is the right answer for it.
      break
    }
  }

  /**
   * After the settle window, re-check the attempted peers and record each
   * outcome. An attempt the probe could judge is marked verified whichever way
   * it went: the probe is deliberately conservative and a "healed" verdict is
   * not solid enough to re-arm a peer on. Anything still dead goes to the
   * dialog.
   *
   * An attempt it could not judge — a peer inside an attach-grace window, which
   * the probe reports as `pending` — is neither spent nor escalated. The row is
   * still written, because how often the verify lands inside grace is what
   * calibrates the settle window against it, but the peer keeps its cheap fix.
   * Treating "no evidence" as failure spent that fix on an attempt nobody had
   * measured, and the next real break skipped straight to the dialog.
   *
   * A genuine recovery can land inside the settle window, and it re-arms every
   * peer. This pass carries the cycle token the attempts were enqueued with so
   * that case is caught: the outcome is still recorded — a fix followed by a
   * recovery is real evidence about that fix — but the peer is not re-spent and
   * nothing is escalated, because there is nothing left to escalate.
   *
   * The token is taken by the caller, not read here. This runs after the fixes
   * have been awaited, and a recovery landing in that window would have bumped
   * the cycle already, so a token read at this point would be the new cycle's
   * and every check below would pass against a cycle these fixes never ran in.
   */
  private scheduleHealVerify(attempts: HealAttempt[], token: number): void {
    // The fixes are awaited before this runs, so the room can have gone away in
    // the meantime; the detector is nulled on dispose.
    if (!this.detector) return
    const settleMs = this.settings.autoHealSettleMS
    const startedAt = Date.now()
    const timer = setTimeout(() => {
      this.autoHealTimers.delete(timer)
      const { recovered, stillDead, pending } = this.evaluateHeal(
        attempts.map((a) => a.identity)
      )
      const latency = Date.now() - startedAt
      const sameCycle = this.autoHealState.isCurrentCycle(token)
      for (const attempt of attempts) {
        const verdict: HealVerdict = recovered.includes(attempt.identity)
          ? 'healed'
          : pending.includes(attempt.identity)
            ? 'pending'
            : 'still_dead'
        this.debugLog('heal_verify', {
          identity: attempt.identity,
          fork: attempt.fork,
          fix: attempt.fix,
          verdict,
          verifyLatencyMs: latency,
          sameCycle,
        })
        this.sink?.emitHealOutcome({
          identity: attempt.identity,
          fork: attempt.fork,
          fix: attempt.fix,
          verdict,
          verifyLatencyMs: latency,
          // Stamped rather than used to suppress the emit: a recovery inside
          // the settle window is worth counting either way, but only a
          // same-cycle verify is evidence about *this* fix. Another peer's
          // reconnect rebuilds every subscription in the room, so the wiring
          // reads clean for reasons this fix had no part in.
          sameCycle,
        })
        // A verdict spends the peer's one cheap fix; an unmeasured attempt
        // gives it back. Spending on `pending` cost the peer a fix nobody had
        // watched work or fail, and sent its next break straight to the dialog.
        if (verdict === 'pending') {
          this.autoHealState.releaseInFlight(attempt.identity, token)
        } else {
          this.autoHealState.markVerified(attempt.identity, token)
        }
      }
      if (sameCycle) {
        this.escalateToDialog(
          attempts.filter((a) => stillDead.includes(a.identity))
        )
      }
    }, settleMs)
    this.autoHealTimers.add(timer)
  }

  /**
   * The reconnect rung, run when the user clicks it in the dialog.
   *
   * This lives here rather than in the dialog because two of its effects have to
   * outlive the component. The reconnect is spent the moment it is attempted, so
   * the next escalation must offer the reload even if the user closed the dialog
   * mid-run; and the peers' flow verdicts describe a subscription that no longer
   * exists once the room has reconnected. A dialog that unmounts halfway would
   * skip both and leave the client stuck on a rung it had already used.
   *
   * There is no verify here on purpose. A flow verdict can only be lifted by
   * energy from the peer's next speaking turn, which will not arrive inside any
   * settle window we are willing to wait, so a verdict-based check would report
   * failure after every reconnect including the ones that worked. The verdicts
   * are cleared instead, and a fault that survived re-condemns the peer when
   * they next speak — that re-condemnation is what escalates them to the reload.
   *
   * The per-peer wiring state is re-armed for the same reason the verdicts are
   * cleared: this is a different room. Leaving those peers spent would skip the
   * cheap fix on their next break and — the reconnect rung now being spent —
   * offer the reload for a fault a play/resink/reattach heals silently.
   *
   * The probe still runs, purely to record what the repair looked like, and it
   * runs *after* the clear. That order is the whole point: probing first reads
   * the very verdicts the reconnect has just invalidated, and since every peer
   * offered this rung for a flow fault carries one, the outcome log would report
   * "still dead" for reconnects that worked. Clearing first leaves the probe
   * measuring the post-reconnect wiring, which is the only thing it can see.
   *
   * All three of the probe's buckets are returned, and the caller logs all
   * three. `pending` is more likely here than anywhere else: a reconnect
   * re-subscribes every peer, so each opens a fresh attach-grace window, and a
   * room slow to resubscribe leaves the whole set unjudgeable when the settle
   * window ends. Reporting only two buckets made those peers vanish from the
   * counts, so the numbers did not add up to the peers that were probed.
   */
  async runReconnect(identities: string[]): Promise<{
    recovered: string[]
    stillDead: string[]
    pending: string[]
  }> {
    // Spent the moment the user asks, before either await. `reconnectRoom`
    // disconnects first, so by the time it settles the room the user had is gone
    // whichever way it went. Marking the rung afterwards left it available on
    // every failure path — a throw skipped the line, a connect that never
    // settled never reached it — so a client whose reconnect could not succeed
    // was offered that same reconnect on every later break and never reached
    // the reload, which is the one repair that works on a room that is gone.
    //
    // `startAudio` is one of those throws, and not an unlikely one: it is a
    // `Promise.all` over `play()` on every attached element in the room, so a
    // single element that will not play rejects the whole call — and this rung
    // is only offered to peers whose playback is already broken. Spending after
    // it left the dialog showing the reload while the controller still thought
    // the reconnect was unused, which is the disagreement splitting these two
    // halves across the dialog and the controller exists to prevent.
    //
    // A recovery landing between this line and `reconnectRoom` settling
    // un-spends the rung, because the recovery edge resets the whole ladder.
    // The unlock below is the likeliest cause of one: it plays every attached
    // element, which is the repair for a peer condemned for a paused element,
    // and the room is still connected and rostered while it runs, so the scan is
    // not gated out the way it is once `reconnectRoom` has torn the room down.
    // Intended, and not re-asserted afterwards: see `AutoHealState.onReconnect`.
    // Everything from here to the end of the settle window is subscription
    // churn this client asked for, so the detector stops calling it dead air.
    // `finally`, because a throw leaves the room in whatever state it reached
    // and a latched flag would blind the detector to `not_subscribed` for the
    // rest of the meeting — a worse failure than the noise this suppresses.
    this.repairInFlight = true
    try {
      this.autoHealState.markReconnectSpent()
      // The gesture boundary. This runs inside the click that opened the repair,
      // so `startAudio()` still has the user activation it needs — and it is the
      // last line here that does. A transient activation expires a few seconds
      // after the click on WebKit and does not survive an await, so anything
      // gesture-dependent added below is already outside it. Nothing there is
      // today: `reconnectRoom` and the settle window need no activation. Put a
      // `setSinkId` past this line and the engine that most needs this repair is
      // the one that will refuse it.
      await this.room?.startAudio()
      await this.meeting.reconnectRoom('dead_air_nudge')
      // The rung is spent, but the per-peer wiring verdicts are not: the room
      // that produced them no longer exists. This one stays after the await
      // because it re-arms peers against a room, and a reconnect that threw did
      // not produce one.
      this.autoHealState.onReconnect()

      const settleMs = this.settings.nudgeSettleMS
      await new Promise((resolve) => setTimeout(resolve, settleMs))
    } finally {
      this.repairInFlight = false
    }

    // Cleared before the probe, never after. `evaluateHeal` judges a peer by
    // re-running the same wiring checks, so leaving the flag up would make a
    // peer that never re-subscribed classify as null — and this method would
    // report the repair recovered precisely when it had failed.
    this.detector?.clearBehavioralVerdicts(identities)
    return this.evaluateHeal(identities)
  }

  /**
   * Non-emitting recovery probe: re-run the wiring checks for the given peers
   * and report which are healthy now, which are still broken, and which cannot
   * be judged yet.
   *
   * All three buckets are passed through. Narrowing to two here silently made
   * every `pending` peer read as still-dead to the callers, which is the one
   * thing the detector's own docstring says not to do: a peer inside its
   * attach-grace window has produced no evidence either way, and forcing that
   * into a binary is the bug the third bucket was added to fix.
   *
   * The no-detector fallback puts everyone in `stillDead` rather than
   * `pending`. A disposed detector is not "ask again later" — there is nobody
   * left to ask — and the callers treat still-dead as the conservative answer.
   */
  private evaluateHeal(identities: string[]): {
    recovered: string[]
    stillDead: string[]
    pending: string[]
  } {
    return (
      this.detector?.evaluateHeal(identities) ?? {
        recovered: [],
        stillDead: identities,
        pending: [],
      }
    )
  }

  /**
   * QA: run the wiring walk now, instead of waiting for the next slide change
   * or the backstop sweep. Staging a fault and then waiting up to 30s to see
   * whether it was noticed is the difference between a testable pipeline and
   * one that can only be confirmed by a production incident.
   *
   * Deliberately not called by {@link qaToggleFault}. Arming a fault and
   * scanning are separate acts because the thing being measured is *how long
   * the real triggers take to notice* — the backstop, a slide change, the next
   * speaking turn. A stager that scanned on its own behalf would answer "does
   * the classifier work", which the unit tests already answer, and destroy the
   * only measurement this surface exists to take.
   *
   * Returns false when detection is not running.
   */
  qaForceScan(): boolean {
    if (!this.detector) return false
    this.detector.scanStructural()
    return true
  }

  /**
   * Record that the dead-air dialog was shown for this request: sends the
   * Sentry nudge bundle. Called by the meeting page when the dialog actually
   * opens, not when the request is emitted, because most requests are debounced
   * or suppressed there and never reach the user.
   */
  reportNudgeShown(request: DeadAirNudgeRequest): void {
    this.sink?.captureNudge(request)
  }

  /**
   * QA: push a synthetic episode through the sink so the GA row can be
   * confirmed end to end without waiting for a real fault. The Sentry bundle
   * follows only if the episode goes on to open the dialog. The
   * dead peer is {@link QA_SYNTHETIC_IDENTITY}, so these are identifiable in
   * both sinks and can be kept out of the real numbers.
   *
   * Returns false when detection is not running.
   */
  qaInjectEpisode(): boolean {
    const env = this.env
    if (!this.sink || !env) return false
    this.sink.emit({
      listenerId: env.listenerId(),
      deadPeers: [
        {
          identity: QA_SYNTHETIC_IDENTITY,
          fork: 'inconclusive',
          wiring: { synthetic: true },
        },
      ],
      peersSubscribed: env.peersSubscribed(),
      peersPublishing: env.roster().length,
      triggeringSpeaker: null,
      pageHidden: env.pageHidden(),
      connectionState: env.connectionState(),
    })
    return true
  }

  /**
   * QA: arm one of the five stageable faults, or clear it if it is already
   * armed.
   *
   * ## Why five and not eight
   *
   * `silent_decode`, `energy_unavailable` and `inconclusive` have no honest
   * stager and are covered by unit tests instead. The first is the interesting
   * one: producing flat decoded energy at the source means publishing near
   * digital silence, and the flow checks that would judge it only run for a
   * peer the SFU has named an active speaker for {@link
   * FirestoreSettingsDeadAirDetector.sustainedSpeakerMS}. Against an
   * `energyEpsilon` of 1e-6 accumulated over a 2500ms turn, the signal has to
   * stay under roughly -64 dBFS to read as flat; LiveKit's active-speaker
   * threshold sits near -35 dBov. There is no overlap, so a source quiet enough
   * to classify is ~30 dB too quiet to ever be evaluated.
   *
   * That is a limit on *staging it from the source*, not on the fork. Real
   * `silent_decode` is a break downstream of the SFU — a wedged decoder, a
   * codec mismatch, comfort noise where speech should be — where the publisher
   * is still loud, still an active speaker, and the bytes still arrive.
   *
   * ## Why every fault is staged on the listener
   *
   * Nothing a publisher can do to their own outbound audio produces dead air
   * that anyone can observe. Stopping their upstream (`pauseUpstream`, or
   * `sender.replaceTrack(null)` under it) also stops the SFU hearing them, which
   * drops them from the active-speaker list, which means the flow checks never
   * run on them. Muting sets `isMuted`, which the wiring walk correctly reads as
   * expected silence. So the five stagers below all break *this* client's
   * reception, and the tester who presses the button is the tester who goes
   * deaf.
   *
   * ## Why faults compose
   *
   * Arming a second fault does not clear the first. `classifyPublication` is a
   * fixed-order chain and reports the first match, so a peer with two faults
   * reports one fork, gets the fix matched to it, and is re-classified by the
   * verify pass as the other. Production has never been observed here at all,
   * so a peer with two simultaneous problems is a case the ladder has to handle
   * and nobody has watched it try. {@link qaClearAllFaults} is the exit.
   */
  async qaToggleFault(fault: DeadAirQaFault): Promise<QaFaultResult> {
    const staged = this.qaStagedFaults.get(fault)
    if (staged) {
      this.qaStagedFaults.delete(fault)
      const undone = await staged.undo()
      this.debugLog('qa_fault_cleared', { fault, detail: undone.detail })
      return { outcome: 'cleared', detail: undone.detail, ok: undone.ok }
    }

    // Breaking a room nothing is watching leaves a tester with silent audio, no
    // episode, and no hint that the fault is still in effect.
    if (!this.detector || !this.room) {
      return {
        outcome: 'refused',
        detail: 'Dead-air detection is not running — nothing to break',
      }
    }

    const result = await this.stageFault(fault, this.room)
    if ('refused' in result) {
      return { outcome: 'refused', detail: result.refused }
    }
    this.qaStagedFaults.set(fault, result)
    this.debugLog('qa_fault_armed', { fault, detail: result.detail })
    return { outcome: 'armed', detail: result.detail }
  }

  /** Which faults are armed right now, for the QA labels. */
  qaArmedFaults(): DeadAirQaFault[] {
    return [...this.qaStagedFaults.keys()]
  }

  /**
   * QA: undo every staged fault.
   *
   * The panic exit. Faults compose on purpose, and a tester who steps away for
   * ten minutes has no other way to answer "what did I leave armed" — the
   * toggles undo themselves individually, but only if you remember which ones
   * you pressed.
   */
  async qaClearAllFaults(): Promise<
    { fault: DeadAirQaFault; detail: string }[]
  > {
    const cleared: { fault: DeadAirQaFault; detail: string }[] = []
    for (const fault of [...this.qaStagedFaults.keys()]) {
      const staged = this.qaStagedFaults.get(fault)
      if (!staged) continue
      this.qaStagedFaults.delete(fault)
      const undone = await staged.undo()
      this.debugLog('qa_fault_cleared', { fault, detail: undone.detail })
      cleared.push({ fault, detail: undone.detail })
    }
    return cleared
  }

  /**
   * QA: report which optional `inbound-rtp` fields this browser actually
   * exposes, per remote peer.
   *
   * The flow forks are gated on fields the spec makes optional. A missing
   * `totalAudioEnergy` makes `evaluateSpeaker` return before it can judge
   * decode at all; a missing `concealedSamples` makes it return before it can
   * run the masked-packet-loss exclusion, so every flat-energy peer degrades to
   * `inconclusive` and `silent_decode` can never be asserted.
   *
   * That means "this browser never reported `silent_decode`" has two readings —
   * healthy, or structurally incapable — and no episode can tell them apart,
   * because on a browser missing the fields no episode is produced. Reading the
   * fields directly is the only way round the circle, and it is one click per
   * platform before anything is staged.
   *
   * Returns null when detection is not running.
   */
  async qaDumpReceiverStats(): Promise<QaReceiverStatsReport[] | null> {
    const room = this.room
    if (!this.detector || !room) return null

    const reports: QaReceiverStatsReport[] = []
    for (const peer of this.qaRemoteAudio(room)) {
      for (const pub of peer.publications) {
        const track = pub.audioTrack
        // `TrackPublication.audioTrack` is typed as the local/remote union, and
        // only the remote half carries a receiver to read stats from.
        if (!track || !('receiver' in track)) continue
        const stats = await unpackAudioReceiverStats(track)
        reports.push({
          identity: peer.identity,
          stats,
          hasEnergy: stats?.totalAudioEnergy !== undefined,
          hasConcealment: stats?.concealedSamples !== undefined,
          hasSamplesReceived: stats?.totalSamplesReceived !== undefined,
          // Both fields, not either: energy decides flat decode, concealment
          // runs the exclusion that separates it from packet loss.
          canReportSilentDecode:
            stats?.totalAudioEnergy !== undefined &&
            stats?.concealedSamples !== undefined,
          // bytesReceived is spec-mandatory, so this one travels everywhere.
          canReportDeliveryStalled: stats !== undefined,
        })
      }
    }
    this.debugLog('qa_receiver_stats', { reports })
    return reports
  }

  /**
   * Apply one fault to the current roster and hand back the undo.
   *
   * Each stager captures exactly what it touched rather than re-deriving it on
   * the way out. The roster moves underneath these — a peer republishing hands
   * back a new track, a reconnect rebuilds every subscription — and an undo
   * that re-walked the room would restore whatever it found instead of what it
   * broke.
   *
   * Nothing here re-applies itself. A fault that auto-heal repairs is *supposed*
   * to disappear: that repair is the behaviour under test, and a stager that
   * re-broke the peer the moment the fix landed would make a working heal
   * impossible to observe.
   */
  private async stageFault(
    fault: DeadAirQaFault,
    room: Room
  ): Promise<StagedQaFault | { refused: string }> {
    const peers = this.qaRemoteAudio(room)

    switch (fault) {
      case 'not_subscribed': {
        // The detector's `not_subscribed` is the *silent* case: a peer the SFU
        // shows publishing that this client has no subscription to. Dropping
        // the subscription locally is exactly that, and it is what the app
        // itself does for quiz sequestration — the difference being that
        // sequestration also raises `isListenerSequestered`, which suppresses
        // the fork on purpose. This path does not, so the fork fires.
        const pubs = peers
          .flatMap((p) => p.publications)
          .filter((pub) => pub.isSubscribed)
        if (pubs.length === 0) {
          return { refused: 'No subscribed remote audio to unsubscribe from' }
        }
        for (const pub of pubs) pub.setSubscribed(false)
        return {
          detail: `${pubs.length} publication(s) unsubscribed`,
          undo: async () => {
            for (const pub of pubs) pub.setSubscribed(true)
            return {
              ok: true,
              detail: `${pubs.length} publication(s) resubscribed`,
            }
          },
        }
      }

      case 'not_attached': {
        // `useMediaTrackBySourceOrName` attaches in an effect keyed on
        // `[track, element]`, so detaching from underneath it does not provoke
        // a re-attach: neither reference changes. The elements stay in the DOM,
        // which is why they can be handed straight back on undo.
        const pairs = peers.flatMap((peer) =>
          peer.publications.flatMap((pub) => {
            const track = pub.audioTrack
            if (!track) return []
            return [...track.attachedElements].map((el) => ({ track, el }))
          })
        )
        if (pairs.length === 0) {
          return { refused: 'No attached remote audio elements to detach' }
        }
        for (const { track, el } of pairs) track.detach(el)
        return {
          detail: `${pairs.length} element(s) detached`,
          undo: async () => {
            for (const { track, el } of pairs) track.attach(el)
            return {
              ok: true,
              detail: `${pairs.length} element(s) re-attached`,
            }
          },
        }
      }

      case 'lost_play': {
        const elements = peers.flatMap((p) => p.elements)
        if (elements.length === 0) {
          return { refused: 'No remote audio elements to pause' }
        }
        for (const el of elements) el.pause()
        return {
          detail: `${elements.length} element(s) paused`,
          undo: async () => {
            const results = await Promise.allSettled(
              elements.map((el) => el.play())
            )
            const refused = results.filter(
              (r) => r.status === 'rejected'
            ).length
            // A resume the browser refused leaves the room silent. Reporting
            // "resumed" there hands the tester a room they believe they
            // repaired, and every later measurement on it is worthless.
            if (refused > 0) {
              return {
                ok: false,
                detail: `Still paused — ${refused} of ${elements.length} element(s) refused to play. Reload the room.`,
              }
            }
            return { ok: true, detail: `${elements.length} element(s) resumed` }
          },
        }
      }

      case 'sink_divergence': {
        return this.stageSinkDivergence(room, peers)
      }

      case 'delivery_stalled': {
        // `setEnabled(false)` asks the SFU to stop forwarding this track to
        // this client. It sets `requestedDisabled` and sends a track update —
        // `isSubscribed` and `isMuted` are untouched, and the element stays
        // attached and playing. So the wiring walk finds nothing wrong while
        // `bytesReceived` freezes, which is the shape the flow check reads as a
        // delivery stall.
        //
        // Staging it on the listener rather than the publisher is what makes it
        // observable: the peer keeps sending to the SFU, so they keep being an
        // active speaker, so the speaker-gated flow check keeps running.
        const pubs = peers
          .flatMap((p) => p.publications)
          .filter((pub) => pub.isSubscribed)
        if (pubs.length === 0) {
          return { refused: 'No subscribed remote audio to stall' }
        }
        for (const pub of pubs) pub.setEnabled(false)
        return {
          detail: `Server forwarding stopped for ${pubs.length} publication(s) — have a peer talk for ~3s`,
          undo: async () => {
            for (const pub of pubs) pub.setEnabled(true)
            return {
              ok: true,
              detail: `Server forwarding restored for ${pubs.length} publication(s)`,
            }
          },
        }
      }
    }
  }

  /**
   * Divert one peer's elements to an output device the room is not using.
   *
   * One peer, not all of them, because of the corroboration rule in
   * `activeSinkAdoptedAnywhere`. When the room's active device is the system
   * default the rule short-circuits and any diverted element is condemned; when
   * a real device has been chosen, the fork is only reported if some *other*
   * element is still on that device, so diverting everything reports nothing.
   * Diverting one peer satisfies both regimes with a single rule, and it is
   * also the fault as it actually happens — one element that failed to
   * re-route while its neighbours succeeded.
   */
  private async stageSinkDivergence(
    room: Room,
    peers: QaPeerAudio[]
  ): Promise<StagedQaFault | { refused: string }> {
    const withElements = peers.filter((p) => p.elements.length > 0)
    const victim = withElements[0]
    if (!victim) {
      return { refused: 'No remote audio elements to re-sink' }
    }
    const sinkable = victim.elements.filter(
      (el) => typeof asSinkable(el).setSinkId === 'function'
    )
    if (sinkable.length === 0) {
      return { refused: 'This browser does not support setSinkId' }
    }

    const active = room.getActiveDevice('audiooutput')
    // A real device with no corroborating element is suppressed by design, so
    // arming here would produce a fault the detector deliberately never
    // reports — indistinguishable, from the button, from one it missed.
    if (normalizeSink(active) !== 'default' && withElements.length < 2) {
      return {
        refused:
          'Needs a second peer with audio: the active output device is not the default, so the fork is suppressed without one still routed to it',
      }
    }

    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices?.enumerateDevices
    ) {
      return { refused: 'No device list available' }
    }
    const devices = await navigator.mediaDevices.enumerateDevices()
    const target = devices.find(
      (d) =>
        d.kind === 'audiooutput' &&
        normalizeSink(d.deviceId) !== normalizeSink(active)
    )
    if (!target) {
      return { refused: 'No alternate audio output device to divert to' }
    }

    const previous = sinkable.map((el) => ({
      el,
      sinkId: asSinkable(el).sinkId ?? '',
    }))
    await Promise.allSettled(
      sinkable.map((el) => asSinkable(el).setSinkId?.(target.deviceId))
    )
    return {
      detail: `${victim.identity}: ${sinkable.length} element(s) diverted to "${target.label || target.deviceId}"`,
      undo: async () => {
        await Promise.allSettled(
          previous.map(({ el, sinkId }) => asSinkable(el).setSinkId?.(sinkId))
        )
        return {
          ok: true,
          detail: `${victim.identity}: ${sinkable.length} element(s) restored`,
        }
      },
    }
  }

  /**
   * The remote audio in the room, grouped by peer.
   *
   * Grouped rather than flattened because `sink_divergence` has to act on one
   * peer and leave the rest alone, and a flat element list cannot express that.
   */
  private qaRemoteAudio(room: Room): QaPeerAudio[] {
    const peers: QaPeerAudio[] = []
    room.remoteParticipants.forEach((participant) => {
      const publications = [...participant.audioTrackPublications.values()]
      if (publications.length === 0) return
      peers.push({
        identity: participant.identity,
        publications,
        elements: publications.flatMap((pub) => [
          ...(pub.audioTrack?.attachedElements ?? []),
        ]),
      })
    })
    return peers
  }

  /**
   * Non-production trace of the detect → fix → verify → escalate chain.
   *
   * The dead-air stack reports to Sentry and GA and to nothing a tester can
   * see, so a manual run has no way to separate "detection never fired" from
   * "it fired and healed silently" from "it fired and the dialog was
   * suppressed". Each of those looks the same from the room: nothing happens.
   *
   * Gated the same way `MeetingCubit.logEvent` gates its own debug line, so
   * production carries neither the calls nor the string building.
   */
  private debugLog(event: string, data?: Record<string, unknown>): void {
    if (this.meeting.repository.inProduction) return
    // eslint-disable-next-line no-console
    console.debug(`[dead_air] ${event}`, data ?? {})
  }

  /**
   * Wait for `detectionEnabled` to arrive and then stand the stack up. Fires at
   * most once; the disposer is tracked so a controller torn down before the
   * setting lands doesn't leave a reaction holding the room.
   */
  private armRetry(room: Room): void {
    if (this.retryArmed) return
    this.retryArmed = true
    this.disposers.push(
      when(
        () => this.settings.detectionEnabled === true,
        () => this.attach(room)
      )
    )
  }

  /**
   * Keep the output-device list fresh so the wiring snapshot can read it
   * synchronously. Refresh once now, then on every `devicechange`; the change
   * time itself is a diagnostic signal (a sink that vanished mid-call).
   */
  private startOutputDeviceCache(room: Room): void {
    void this.refreshOutputDeviceCache(room, null)
    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices?.addEventListener
    ) {
      return
    }
    const onChange = () => {
      void this.refreshOutputDeviceCache(room, Date.now())
    }
    navigator.mediaDevices.addEventListener('devicechange', onChange)
    this.deviceChangeUnsub = () =>
      navigator.mediaDevices.removeEventListener('devicechange', onChange)
  }

  private async refreshOutputDeviceCache(
    room: Room,
    changedAt: number | null
  ): Promise<void> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const audioOutputs = devices
        .filter((d) => d.kind === 'audiooutput')
        .map((d) => ({ deviceId: d.deviceId, label: d.label }))
      this.outputSnapshot = {
        audioOutputs,
        activeSinkId: room.getActiveDevice('audiooutput'),
        lastDeviceChangeAt: changedAt ?? this.outputSnapshot.lastDeviceChangeAt,
      }
    } catch {
      // enumerateDevices can reject (permissions, no navigator); leave the last
      // good cache in place rather than blanking it.
    }
  }
}
